import React, { ChangeEvent, Fragment, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Box, Button, Divider, Grid, IconButton, SelectChangeEvent } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import {
  addElementIdToRecipient,
  removeElementIdFromRecipients,
  selectedElement,
  updateContentElementById,
} from 'features/document/documentSlice';
import { AzulCheckbox, AzulTextField } from '@the-ksquare-group/azul-ds';
import { isDropdownElement } from 'utils/typeGuards';
import { v4 as uuidv4 } from 'uuid';
import { DropdownOptionItem, Recipient } from 'features/document/constants';
import { colorPalette, docGeniusPalette } from 'styles/partials/colors';
import { selectRecipients } from 'features/document/documentSlice';
import RecipientSelect from 'components/RecipientSelect/RecipientSelect';
import { Subtitle, Title } from '../../Sidebar.styled';

interface DropdownSidebarProps {
  currentElement: selectedElement;
}

export default function DropdownSidebar({ currentElement }: DropdownSidebarProps) {
  if (!isDropdownElement(currentElement.data)) {
    return null;
  }
  const dispatch = useAppDispatch();

  const { labelText, required } = currentElement.data.content;
  const {
    data: { recipient: currentRecipient },
  } = currentElement;

  const [dropdownOptions, setDropdownOptions] = useState<DropdownOptionItem[]>(currentElement.data.content.elements);
  const isDeleteButtonDisabled = dropdownOptions.length === 1;

  const recipients = useAppSelector(selectRecipients);
  const [recipient, setRecipient] = useState<Recipient>(currentRecipient || null);

  const handleOnChangeRecipient = (event: SelectChangeEvent) => {
    dispatch(removeElementIdFromRecipients(currentElement.data.id));

    const selectedRecipient = recipients.find(recipient => recipient.fullName === event.target.value);
    if (selectedRecipient) {
      setRecipient(selectedRecipient);

      dispatch(addElementIdToRecipient({ recipientId: selectedRecipient.id, elementId: currentElement.data.id }));

      dispatch(
        updateContentElementById({
          content: {},
          style: {},
          recipient: selectedRecipient,
        })
      );
    }
  };

  const handleRequiredProperty = (property: string) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateContentElementById({
          content: {
            [property]: event.target.checked,
          },
          style: {},
        })
      );
    };
  };

  const handleLabelTextProperty = (property: string) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateContentElementById({
          content: {
            [property]: event.target.value,
          },
          style: {},
        })
      );
    };
  };

  const handleUpdateDropdownItem = (updatedDropdownOptions: DropdownOptionItem[]) => {
    dispatch(
      updateContentElementById({
        content: {
          elements: updatedDropdownOptions,
        },
        style: {},
      })
    );
    setDropdownOptions(updatedDropdownOptions);
  };

  const handleAddDropdownOption = () => {
    const newDropdownOptionId = uuidv4();
    const newDropdownOption = { id: newDropdownOptionId, option: `New Option`, value: 'New Value' };

    handleUpdateDropdownItem([...dropdownOptions, newDropdownOption]);
  };

  const handleRemoveDropdownOption = (id: string) => {
    const updatedDropdownOptions = dropdownOptions.filter(dropdown => dropdown.id !== id);
    handleUpdateDropdownItem(updatedDropdownOptions);
  };

  const handleOptionChange = (id: string, newOption: string) => {
    const updatedDropdownOptions = dropdownOptions.map(dropdown =>
      dropdown.id === id ? { ...dropdown, option: newOption } : dropdown
    );
    handleUpdateDropdownItem(updatedDropdownOptions);
  };

  const handleValueChange = (id: string, newValue: string) => {
    const updatedDropdownOptions = dropdownOptions.map(dropdown =>
      dropdown.id === id ? { ...dropdown, value: newValue } : dropdown
    );
    handleUpdateDropdownItem(updatedDropdownOptions);
  };

  return (
    <Grid container direction='row' sx={{ width: '100%' }} gap={2}>
      <Grid item xs={12}>
        <Title>Dropdown Block</Title>
      </Grid>
      <Box display={'flex'} flexDirection={'column'} gap={'24px'} marginTop={'24px'} width='100%'>
        <Subtitle>Recipient</Subtitle>
        <RecipientSelect
          value={recipient}
          recipientOptions={recipients}
          editRecipients={false}
          onChange={handleOnChangeRecipient}
        />
      </Box>
      <Divider
        color='#C9CACB'
        flexItem
        orientation='horizontal'
        variant='middle'
        sx={{ margin: '8px 0 0', width: '100%' }}
      />
      <Grid color={colorPalette.darkGray1} item xs={12}>
        <AzulCheckbox value={required} label='Required' onChange={handleRequiredProperty('required')} />
      </Grid>
      <Grid item xs={12}>
        <Subtitle style={{ display: 'flex', marginBottom: '16px' }}>Label Text</Subtitle>
        <AzulTextField
          type='text'
          label=''
          placeholder='Add Label Text'
          name='labelTextInput'
          value={labelText}
          onChange={handleLabelTextProperty('labelText')}
        />
      </Grid>
      <Grid item xs={4}>
        <Subtitle>Option</Subtitle>
      </Grid>
      <Grid item xs={4}>
        <Subtitle>Value</Subtitle>
      </Grid>
      {dropdownOptions.map(({ id, option, value }) => (
        <Fragment key={id}>
          <Grid item xs={4}>
            <AzulTextField
              id={`dropdown-option-${id}`}
              type='text'
              label=''
              placeholder='Option'
              name='optionInput'
              value={option}
              onChange={e => handleOptionChange(id, e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <AzulTextField
              id={`dropdown-value-${id}`}
              type='text'
              label=''
              placeholder='Value'
              name='valueInput'
              value={value}
              onChange={e => handleValueChange(id, e.target.value)}
            />
          </Grid>
          <Grid item xs={2}>
            <IconButton
              aria-label={`Remove Option ${id}`}
              disabled={isDeleteButtonDisabled}
              color='primary'
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
              onClick={() => handleRemoveDropdownOption(id)}
            >
              <DeleteRoundedIcon sx={{ width: '24px', height: '24px', margin: '8px' }} />
            </IconButton>
          </Grid>
        </Fragment>
      ))}
      <Grid item xs={12}>
        <Button
          variant='outlined'
          startIcon={<AddRoundedIcon />}
          sx={{ marginTop: '16px', textTransform: 'none', borderColor: docGeniusPalette.primaryBlue }}
          onClick={handleAddDropdownOption}
        >
          Add Option
        </Button>
      </Grid>
    </Grid>
  );
}
